import {
  CreateTaxRateDto,
  LocationTaxRateEntity,
  TaxRateAssignment,
  TaxRateDraft,
  TaxRateEntity,
  TaxRateSource,
  TaxRateType,
} from '../types/taxRate.types';

/**
 * Tax rate units. A leaf module: it imports types only, so it is safe to
 * reach for from anywhere, eagerly loaded files included.
 *
 * A percentage is stored times 10.000 (`88750` is 8.875%), a fixed rate in
 * cents per item (`150` is $1.50). Both share one integer field with the same
 * bounds, which is why the maximum below means 100% for one type and $10.000
 * for the other.
 */

/** A percentage point is 10.000 in `rate`. */
export const TAX_RATE_PERCENT_SCALE = 10000;

/** Backend bound on `rate`, whatever the type. Always a whole number. */
export const TAX_RATE_MIN = 0;
export const TAX_RATE_MAX = 1000000;

/** 100% - the same ceiling read as a percentage. */
export const TAX_RATE_MAX_PERCENT = TAX_RATE_MAX / TAX_RATE_PERCENT_SCALE;

/** 8.875 -> 88750. Rounded: the field takes integers only. */
export const percentToTaxRate = (percent: number) =>
  Math.round(percent * TAX_RATE_PERCENT_SCALE);

/** 88750 -> 8.875 */
export const taxRateToPercent = (rate: number) => rate / TAX_RATE_PERCENT_SCALE;

/**
 * What the rate reads as: `8.875%` or `$1.50`. Trailing zeros are dropped from
 * the percentage (`9%`, not `9.0000%`) - `rate / scale` already gives the
 * shortest exact decimal.
 */
export const taxRateAmountLabel = (
  taxRate: Pick<TaxRateEntity, 'rate' | 'type'>
) =>
  taxRate.type === TaxRateType.percentage
    ? `${taxRateToPercent(taxRate.rate)}%`
    : `$${(taxRate.rate / 100).toFixed(2)}`;

/** `NY Sales Tax 8.875%` - the rate with its name, for a one-line summary. */
export const taxRateLabel = (
  taxRate: Pick<TaxRateEntity, 'name' | 'rate' | 'type'>
) => `${taxRate.name} ${taxRateAmountLabel(taxRate)}`;

/** Synced from the POS, so read-only wherever it shows up. */
export const isPosTaxRate = (taxRate: Pick<TaxRateEntity, 'source'>) =>
  taxRate.source === TaxRateSource.pos;

/**
 * A rate may be edited only when the backend says so AND the location is not
 * a POS one - on a POS location every write answers 400, whatever the row
 * says.
 */
export const canEditTaxRate = (
  taxRate: Pick<LocationTaxRateEntity, 'editable' | 'source'>,
  isPosLocation: boolean
) => !isPosLocation && taxRate.editable && !isPosTaxRate(taxRate);

/**
 * The two rules `POST /v1/locations/:id/verify` refuses a location for, said
 * before the request goes out and in the backend's own words. Returns the
 * first problem, or null when the rates are good to send.
 *
 * A location cannot be verified without taxes at all, and a rate that only
 * applies where it is assigned (an alcohol tax, say) is not enough on its own:
 * something has to cover the rest of the menu.
 */
export const validateVerificationTaxRates = (
  taxRates: Pick<TaxRateEntity, 'applyToAllItems'>[]
): string | null => {
  if (taxRates.length === 0)
    return 'At least one tax rate is required to verify a location.';

  if (!taxRates.some((taxRate) => taxRate.applyToAllItems))
    return 'At least one tax rate has to apply to all items.';

  return null;
};

/** Drops the local `key` - the API never sees it. */
export const taxRateDraftToDto = ({
  key,
  ...taxRate
}: TaxRateDraft): CreateTaxRateDto => taxRate;

/**
 * Assignments grouped for display: by brand, and inside a brand by menu when
 * the rows carry one (multi menu). The location's own brand comes first, the
 * virtual ones after it in the order the backend sent them.
 */
export type TaxRateAssignmentGroup = {
  brandId: number;
  brandName: string;
  isLocation: boolean;
  /** One entry per menu, or a single unnamed entry before multi menu. */
  menus: {
    menuId: number | null;
    menuName: string | null;
    assignments: TaxRateAssignment[];
  }[];
};

export const groupTaxRateAssignments = (
  assignments: TaxRateAssignment[]
): TaxRateAssignmentGroup[] => {
  const groups: TaxRateAssignmentGroup[] = [];

  assignments.forEach((assignment) => {
    let group = groups.find((g) => g.brandId === assignment.brandId);
    if (!group) {
      group = {
        brandId: assignment.brandId,
        brandName: assignment.brandName,
        isLocation: assignment.isLocation,
        menus: [],
      };
      groups.push(group);
    }

    const menuId = assignment.menuId ?? null;
    let menu = group.menus.find((m) => m.menuId === menuId);
    if (!menu) {
      menu = {
        menuId,
        menuName: assignment.menuName ?? null,
        assignments: [],
      };
      group.menus.push(menu);
    }

    menu.assignments.push(assignment);
  });

  // The location's own brand reads as the heading of the list; virtual brands
  // keep the order they arrived in.
  return groups.sort((a, b) => Number(b.isLocation) - Number(a.isLocation));
};
